import React, { useState, useEffect, useRef, useCallback } from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import {
    Box,
    Drawer as MuiDrawer,
    CssBaseline,
    IconButton,
    Divider,
    Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

const DEFAULT_DRAWER_WIDTH = 240;
const MIN_DRAWER_WIDTH = 180;
const MAX_DRAWER_WIDTH = 400;
const COLLAPSED_DRAWER_WIDTH = 65; // Width when sidebar is collapsed

const openedMixin = (theme: Theme, width: number): CSSObject => ({
    width: `${width}px`,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `${COLLAPSED_DRAWER_WIDTH}px`,
    [theme.breakpoints.up('sm')]: {
        width: `${COLLAPSED_DRAWER_WIDTH}px`,
    },
});

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

interface DrawerProps {
    theme?: Theme;
    open?: boolean;
    width?: number;
}

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open' && prop !== 'width'
})(
    ({ theme, open = false, width = DEFAULT_DRAWER_WIDTH }: DrawerProps) => {
        if (!theme) {
            throw new Error('Theme is required for Drawer component');
        }

        return {
            width: open ? `${width}px` : `${COLLAPSED_DRAWER_WIDTH}px`,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            boxSizing: 'border-box',
            ...(open && {
                ...openedMixin(theme, width),
                '& .MuiDrawer-paper': openedMixin(theme, width),
            }),
            ...(!open && {
                ...closedMixin(theme),
                '& .MuiDrawer-paper': closedMixin(theme),
            }),
        };
    }
);

interface SidebarDrawerProps {
    sidebarContent: React.ReactNode;
    mainContent: React.ReactNode;
    defaultOpen?: boolean;
    defaultPinned?: boolean;
    defaultWidth?: number;
}

const SidebarDrawer: React.FC<SidebarDrawerProps> = ({
    sidebarContent,
    mainContent,
    defaultOpen = true,
    defaultPinned = true,
    defaultWidth = DEFAULT_DRAWER_WIDTH
}) => {
    const [open, setOpen] = useState(defaultOpen);
    const [pinned, setPinned] = useState(defaultPinned);
    const [drawerWidth, setDrawerWidth] = useState(defaultWidth);
    const [isResizing, setIsResizing] = useState(false);
    const resizeRef = useRef<number | null>(null);

    // Handle drawer open/close toggle
    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    // Handle drawer pin/unpin toggle
    const handlePinToggle = () => {
        setPinned(!pinned);
    };

    // Handle mouse down for resizing
    const handleMouseDown = (e: React.MouseEvent) => {
        setIsResizing(true);
        resizeRef.current = e.clientX;
    };

    // Handle mouse move for resizing
    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizing || resizeRef.current === null) return;

        const delta = e.clientX - resizeRef.current;
        const newWidth = Math.max(MIN_DRAWER_WIDTH, Math.min(MAX_DRAWER_WIDTH, drawerWidth + delta));

        setDrawerWidth(newWidth);
        resizeRef.current = e.clientX;
    }, [isResizing, drawerWidth]);

    // Handle mouse up to stop resizing
    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
        resizeRef.current = null;
    }, []);

    // Add and remove event listeners for resizing
    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

// We need to clone the sidebar content to pass the `sidebarCollapsed` prop.
const sidebarWithProps = React.isValidElement(sidebarContent)
    ? React.cloneElement(sidebarContent as React.ReactElement<any>, { sidebarCollapsed: !open })
    : sidebarContent;

// Handle drawer hover events for auto-expand/collapse when not pinned
const handleDrawerMouseEnter = () => {
    if (!pinned && !open) {
        setOpen(true);
    }
};

const handleDrawerMouseLeave = () => {
    if (!pinned && open) {
        setOpen(false);
    }
};

return (
    <Box sx={{ display: 'flex', height: '100%' }}>
        <CssBaseline />
        <Drawer
            variant="permanent"
            open={open}
            width={drawerWidth}
            onMouseEnter={handleDrawerMouseEnter}
            onMouseLeave={handleDrawerMouseLeave}
        >
            <DrawerHeader>
                <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tooltip title={pinned ? 'Unpin sidebar' : 'Pin sidebar'}>
                        <IconButton onClick={handlePinToggle}>
                            {pinned ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={open ? 'Collapse sidebar' : 'Expand sidebar'}>
                        <IconButton onClick={handleDrawerToggle}>
                            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </DrawerHeader>
            <Divider />
            {sidebarWithProps}
            {open && (
                <Box
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '5px',
                        cursor: 'ew-resize',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        },
                        ...(isResizing && {
                            backgroundColor: 'primary.main',
                            opacity: 0.5,
                            width: '5px',
                        }),
                    }}
                    onMouseDown={handleMouseDown}
                />
            )}
        </Drawer>
        <Box
            component="main"
            sx={{
                flexGrow: 1,
                p: 3,
                width: { xs: '100%' },
                maxWidth: '100%',
                overflowX: 'hidden',
                ml: { xs: open ? `${drawerWidth}px` : `${COLLAPSED_DRAWER_WIDTH}px` },
                transition: (theme) => theme.transitions.create('margin', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            }}
        >
            <Box sx={{ maxWidth: '1200px', mx: 'auto', width: '100%' }}>
                {mainContent}
            </Box>
        </Box>
    </Box>
);
};

export default SidebarDrawer;
